import { useFetchMyWeeklyBudget } from '../../../hooks/useFetchMyWeeklyBudget';
import { useEffect, useMemo, useState } from 'react';
import { ITransaction, IWeeklyTransaction } from '../../../interface/transaction';
import { useTranslation } from 'react-i18next';
import { useFetchDashboardTransactions } from '../../../hooks/useFetchTransactions';
import dayjs from 'dayjs';
import { useUser } from '../../../context/UserContext';
import TransactionCard from '../transactionCard';
import isoWeek from 'dayjs/plugin/isoWeek';
import { formatGroupHours, formatter, getWeekDateRange } from '../../../constants/utilityFunctions';
import { ROUTES, UserType } from '../../../constants/constants';
import NoData from '../noData';
import { useLanguage } from '../../../context/LanguageContext';
import { useQueryClient } from '@tanstack/react-query';
import SearchInputWithFilters from '../SearchInputWithFilters';
import { TransactionFilters, TransactionStatus } from '../../../constants/transaction';
import RenderTransactionRestrictions from '../addTransaction/renderTransactionRestrictions';
import { Divider, Paragraph, Spin } from '../../Ant';
import { Flex } from 'antd';
import { CustomLegend } from '../../../assets/styles/components/dashboard.styled';
import WeeklyBudgetGraph from '../graphs/weeklyBudgetGraphs';
import CollapsibleChartPanel from '../../collapsibleChartPanel/collapsibleChartPanel';
import { formatChartWeekRange } from '../graphs/chartPanelSummary';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useSearchParams } from 'react-router-dom';
import { useTransaction } from '../../../context/TransactionContext';
import { useCopyTransaction } from '../../../hooks/useCopyTransaction';
import { IActiveSortState, ISortOption } from '../../../interface/common';
import SortSelector from '../../sortPopover/sortSelector';
import SkeletonTransactionList from '../../common/skeleton/skeletonTransactionList';
import SkeletonChart from '../../common/skeleton/skeletonChart';
import CollapsibleListLayout from '../../collapsibleListLayout/collapsibleListLayout';
import { DocumentTextEmpty } from '../../icons';
import { useDashboardChartVisibility } from '../../../hooks/useDashboardChartVisibility';
import CollapsibleDateGroup from '../../collapsibleDateGroup/collapsibleDateGroup';
import {
  DateGroupList,
  toWeekGroupKey,
  useCollapsedDateGroups,
} from '../../../hooks/useCollapsedDateGroups';
import { useFetchAllPages } from '../../../hooks/useFetchAllPages';

dayjs.extend(isoWeek); // Enable ISO week support (Monday as start of the week)

interface IWeekTransactionGroup {
  key: string;
  weekStart: dayjs.Dayjs;
  totalBillable: number;
  transactions: ITransaction[];
}

const buildDefaultWeeklyDateRange = () => ({
  startDate: dayjs().startOf('isoWeek').subtract(3, 'week').format('YYYY-MM-DD'),
  endDate: dayjs().endOf('isoWeek').format('YYYY-MM-DD'),
});

const WeeklyBudget = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { push } = useNavigationStack();
  const { user } = useUser();
  const { isChartVisible, toggleChartVisibility } = useDashboardChartVisibility();
  const queryClient = useQueryClient();
  const { updateTransaction } = useTransaction();
  const hidePricingInfo =
    user?.userRole?.hidePricingInformation === true || user?.userType === UserType.RESTRICTED;
  const { data: weeklyBudgetTransactions, isLoading: isLoadingWeeklyBudget } =
    useFetchMyWeeklyBudget();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSelectedWeek = searchParams.get('selectedWeek');
  const initialSearch = searchParams.get('weeklysearch') || '';
  const transactionFilters = [
    { label: t('transaction:filter.allTransactions'), value: TransactionFilters.all },
    { label: t('transaction:filter.openTransactions'), value: TransactionFilters.open },
  ];
  const initialFilterValue = searchParams.get('weeklyfilter');
  const initialFilter = transactionFilters.find((opt) => opt.value === initialFilterValue);
  const [copyClicked, setCopyClicked] = useState<boolean>();
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const [selectedWeek, setSelectedWeek] = useState<string | null>(initialSelectedWeek ?? null);
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const { copyTransaction, startTransactionOnSameMatter } = useCopyTransaction({
    setCopyClicked,
    setSelectedTransaction,
  });
  const [selectedFilter, setSelectedFilter] = useState<{ label: string; value: string }>(
    initialFilter ?? {
      label: t('transaction:filter.allTransactions'),
      value: TransactionFilters.all,
    },
  );
  const [dateRange, setDateRange] = useState(buildDefaultWeeklyDateRange);
  const [formatedWeeklyTransaction, setFormatedWeeklyTransaction] = useState<IWeeklyTransaction[]>(
    [],
  );
  const [activeSort, setActiveSort] = useState<IActiveSortState>({
    field: 'transactionDate',
    direction: 'DESC',
  });

  useEffect(() => {
    if (weeklyBudgetTransactions.length > 0) {
      const formattedTransactions: IWeeklyTransaction[] = weeklyBudgetTransactions.map(
        (hour: IWeeklyTransaction) => ({
          ...hour,
          formatedWeek: `${t('common:week')} ${hour.weekNumber}`,
        }),
      );
      setFormatedWeeklyTransaction(formattedTransactions);
    }
  }, [weeklyBudgetTransactions, language]);
  useEffect(() => {
    if (selectedWeek && formatedWeeklyTransaction.length > 0) {
      const transaction = formatedWeeklyTransaction.find(
        (val) => val.formatedWeek === selectedWeek,
      );
      if (transaction) {
        const { weekStart, weekEnd } = getWeekDateRange(transaction.year, transaction.weekNumber);
        setDateRange({
          startDate: weekStart,
          endDate: weekEnd,
        });
      }
    }
  }, [selectedWeek, formatedWeeklyTransaction]);
  useEffect(() => {
    if (isChartVisible) return;
    setSelectedWeek(null);
    setDateRange(buildDefaultWeeklyDateRange());
  }, [isChartVisible]);
  const totalActualHours = useMemo((): number => {
    return weeklyBudgetTransactions.reduce(
      (sum: number, transaction: IWeeklyTransaction) => sum + transaction.billableHours,
      0,
    );
  }, [weeklyBudgetTransactions]);
  const totalBudgetedHours = useMemo((): number => {
    return weeklyBudgetTransactions.reduce(
      (sum: number, transaction: IWeeklyTransaction) => sum + transaction.budgetedHours,
      0,
    );
  }, [weeklyBudgetTransactions]);
  const chartSubtitle = useMemo(() => {
    const weekRange = formatChartWeekRange(weeklyBudgetTransactions, t('common:week'));
    if (!weekRange) return undefined;
    const hoursSummary = t('common:chart.hoursOfBudgeted', {
      actual: formatter.regionalNumberFormat({ value: totalActualHours }),
      budgeted: formatter.regionalNumberFormat({ value: totalBudgetedHours }),
    });
    return (
      <>
        <span>{weekRange}</span>
        <span className="chart-panel-subtitle-separator" />
        <span>{hoursSummary}</span>
      </>
    );
  }, [weeklyBudgetTransactions, totalActualHours, totalBudgetedHours, t]);
  useEffect(() => {
    setSearchParams({
      weeklysearch: searchValue,
      weeklyfilter: selectedFilter?.value || '',
      selectedWeek: selectedWeek || '',
    });
  }, [searchValue, selectedFilter, selectedWeek]);
  const transactionSortOptions: ISortOption[] = useMemo(
    () => [
      {
        field: 'transactionDate',
        label: t('transaction:transactionDate'),
        type: 'date',
        defaultDirection: 'DESC',
      },
    ],
    [t],
  );
  const {
    data: transactionList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    refetch,
    isLoading: isLoadingWeeklyTransactions,
    isFetching,
  } = useFetchDashboardTransactions({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    pageSize: 50,
    search: searchValue,
    relations: [
      'matter:matterNumber,subject,currency,invoiceableType,language',
      'principalCompany:name',
      'principalPrivatePerson:fullName',
      'unit:translations',
      'transactionType',
    ],
    extraFilters: {
      transactionUserId: user?.id ?? '',
      ...(selectedFilter.value.includes(TransactionFilters.open)
        ? { status: TransactionStatus.open }
        : {}),
    },
    fields: [
      'billableQuantity',
      'totalPrice',
      'totalPriceInCurrency',
      'unitId',
      'unitPrice',
      'unitPriceInCurrency',
      'status',
      'subject',
      'transactionDate',
      'matterId',
      'workedHours',
      'hasImportantNotes',
      'hasNotes',
      'createdOn',
    ],
    sortBy: [`${activeSort.field}:${activeSort.direction}`],
    enabled: !!user?.id,
  });
  useFetchAllPages({ hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage });
  const { isCollapsed, toggle, expand, getToggleAllControl } = useCollapsedDateGroups(
    DateGroupList.weeklyBudget,
  );
  const groupedTransactions = useMemo((): IWeekTransactionGroup[] => {
    // Create a map from API response for quick lookup
    const billableHoursMap = weeklyBudgetTransactions.reduce(
      (acc: Record<number, number>, entry: IWeeklyTransaction) => {
        acc[entry.weekNumber] = entry.billableHours;
        return acc;
      },
      {},
    );

    // Sort transactions based on activeSort, latest created first for equal dates
    const sortedTransactions = [...transactionList].sort((a, b) => {
      const dateDiff = dayjs(a.transactionDate).diff(dayjs(b.transactionDate));
      if (dateDiff !== 0) return activeSort.direction === 'ASC' ? dateDiff : -dateDiff;
      return dayjs(b.createdOn).diff(dayjs(a.createdOn));
    });

    const grouped = sortedTransactions.reduce(
      (acc, transaction) => {
        const weekStart = dayjs(transaction?.transactionDate).startOf('isoWeek');
        const key = toWeekGroupKey(weekStart);
        acc[key] ??= {
          key,
          weekStart,
          totalBillable: billableHoursMap[weekStart.isoWeek()] ?? 0,
          transactions: [],
        };
        acc[key].transactions.push(transaction);
        return acc;
      },
      {} as Record<string, IWeekTransactionGroup>,
    );

    return (Object.values(grouped) as IWeekTransactionGroup[]).sort((a, b) =>
      activeSort.direction === 'ASC'
        ? a.weekStart.diff(b.weekStart)
        : b.weekStart.diff(a.weekStart),
    );
  }, [transactionList, weeklyBudgetTransactions, activeSort]);
  const formatWeekTitle = (weekStart: dayjs.Dayjs) =>
    `${t('common:week')} ${weekStart.isoWeek()} (${formatter.dateWithoutYear(weekStart.toDate())} - ${formatter.dateWithoutYear(weekStart.endOf('isoWeek').toDate())})`;
  const handleAdd = () => {
    push(ROUTES.ADD_EDIT_TRANSACTION);
  };
  const handleClickedWeek = (week: string) => {
    const transaction = formatedWeeklyTransaction.find((val) => val.formatedWeek === week);
    if (transaction) {
      const { weekStart, weekEnd } = getWeekDateRange(transaction.year, transaction.weekNumber);
      setDateRange({
        startDate: weekStart,
        endDate: weekEnd,
      });
      expand(toWeekGroupKey(weekStart));
    }
  };
  const handleSelectTransaction = (transaction?: ITransaction) => {
    if (transaction && !copyClicked) {
      push(`${ROUTES.ADD_EDIT_TRANSACTION}/${transaction.id}`);
    } else if (transaction && copyClicked) {
      push(ROUTES.ADD_EDIT_TRANSACTION);
    } else {
      updateTransaction(null);
      setCopyClicked(false);
      setSelectedTransaction(undefined);
    }
  };
  const handleCopyTransaction = (transaction: ITransaction) => {
    copyTransaction(transaction);
  };
  const renderLoader = () => {
    if (isFetching) {
      return <Spin />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="weekly-budget-listing"
      contentGap="gutter"
      contentClassName="items-center w-full"
      header={
        <SearchInputWithFilters
          dateGroupToggle={getToggleAllControl(groupedTransactions.map((group) => group.key))}
          handleAdd={handleAdd}
          focusAddOnEntry
          tooltipTextForAdd={t('transaction:addTransaction')}
          searchTitle={t('transaction:search')}
          searchValue={searchValue}
          filterTitle={t('transaction:filter.label')}
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          handleRefresh={() => {
            queryClient.removeQueries({ queryKey: ['transactions'] });
            return refetch();
          }}
          radioOptions={transactionFilters}
          selectedFilter={[selectedFilter]}
          setSelectedFilter={(val) => setSelectedFilter(val)}
          clearFilter={() => {
            setSelectedFilter({
              label: t('transaction:filter.allTransactions'),
              value: TransactionFilters.all,
            });
          }}
        >
          <SortSelector
            modalLabel={t('common:sorting.title')}
            options={transactionSortOptions}
            activeSort={activeSort}
            onApply={(sort) => setActiveSort(sort)}
          />
        </SearchInputWithFilters>
      }
    >
      <CollapsibleChartPanel
        title={t('common:chart.actualVsBudgetHours')}
        subtitle={chartSubtitle}
        expanded={isChartVisible}
        onToggle={toggleChartVisibility}
        testId="weekly-budget-chart-panel"
      >
        {isLoadingWeeklyBudget ? (
          <SkeletonChart legendItems={2} />
        ) : (
          <>
            <WeeklyBudgetGraph
              formatedWeeklyTransaction={formatedWeeklyTransaction}
              selectedWeek={selectedWeek}
              setSelectedWeek={setSelectedWeek}
              setDateRange={setDateRange}
              handleClickedWeek={handleClickedWeek}
            />
            <Divider className="mb-8" />
            <CustomLegend justify="center" align="center" gap={5} className="weekly-budget-legend">
              <div className="d-flex items-center">
                <span className="primary-bar-color"></span>
                <Paragraph
                  data-testid="actual-hours"
                  $fontsize="12px"
                  $color="description"
                  $lineheight="18px"
                >
                  {t('transaction:dashboardActualHours')}:
                  <span className="legend-data">
                    {formatter.regionalNumberFormat({
                      value: totalActualHours,
                      minimumFractionDigits: 2,
                    })}{' '}
                    {t('common:h')}
                  </span>
                </Paragraph>
              </div>
              <div className="d-flex items-center">
                <span className="primary-bar-color secondary-bar"></span>
                <Paragraph
                  data-testid="budget-hours"
                  $fontsize="12px"
                  $color="description"
                  $lineheight="18px"
                >
                  {t('transaction:dashboardBudgetHours')}:
                  <span className="legend-data">
                    {formatter.regionalNumberFormat({
                      value: totalBudgetedHours,
                      minimumFractionDigits: 2,
                    })}{' '}
                    {t('common:h')}
                  </span>
                </Paragraph>
              </div>
            </CustomLegend>
          </>
        )}
      </CollapsibleChartPanel>
      {groupedTransactions && Object.entries(groupedTransactions).length > 0 && (
        <Flex vertical className="w-full gap-gutter">
          {groupedTransactions.map((group) => (
            <CollapsibleDateGroup
              key={group.key}
              title={formatWeekTitle(group.weekStart)}
              itemCount={t('transaction:transactionCount', { count: group.transactions.length })}
              total={formatGroupHours(group.totalBillable, t)}
              collapsed={isCollapsed(group.key)}
              onToggle={() => toggle(group.key)}
            >
              {group.transactions.map((transaction) => (
                <TransactionCard
                  key={transaction.id}
                  transaction={transaction}
                  hidden={hidePricingInfo}
                  onEdit={(transaction) => {
                    setSelectedTransaction(transaction);
                  }}
                  onCopy={(transaction) => {
                    handleCopyTransaction(transaction);
                  }}
                  onNewToSameMatter={startTransactionOnSameMatter}
                />
              ))}
            </CollapsibleDateGroup>
          ))}
        </Flex>
      )}
      {groupedTransactions.length === 0 && !isLoadingWeeklyTransactions && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={t('common:noData')}
            subHeader={t('transaction:noDataContent')}
            icon={<DocumentTextEmpty />}
          />
        </div>
      )}
      {isLoadingWeeklyTransactions ? <SkeletonTransactionList /> : renderLoader()}
      <RenderTransactionRestrictions
        transaction={selectedTransaction}
        onSelectTransaction={handleSelectTransaction}
      />
    </CollapsibleListLayout>
  );
};

export default WeeklyBudget;
